import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useEffect, useState, FormEvent } from "react";
import AppleIcon from "@mui/icons-material/Apple";
import { Dialog, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  getAuth,
  OAuthProvider,
  GoogleAuthProvider,
  signInWithCredential,
  signInWithEmailAndPassword,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { parentsFirebase } from "app/Auth";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition, WarningMessageBox, UserAvatar } from "common/elements";
import I18n from "features/parent/i18n";
import { UserProvider } from "features/user/types";
import { updateLastSignInTime } from "features/user/api";
import { GoogleSignButton } from "features/auth/GoogleSignButton";

interface ReAuthModalProps {
  open: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

type ReAuthView = "password" | "reset" | "sent";

export const ReAuthModal = ({ open, onSuccess, onClose }: ReAuthModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [disable, setDisable] = useState(false);
  const [view, setView] = useState<ReAuthView>("password");
  const parent = useSelector((state: RootState) => state.user.parent);
  const auth = getAuth(parentsFirebase);
  const currentUser = auth.currentUser;
  const providerId = currentUser?.providerData[0]?.providerId;

  useEffect(() => {
    if (open) {
      setPassword("");
      setShowPassword(false);
      setPasswordError(false);
      setAuthError(false);
      setDisable(false);
      setView("password");
    }
  }, [open]);

  // SSO メッセージハンドラ
  useEffect(() => {
    if (!open) return;

    const handleMessage = (message: MessageEvent) => {
      try {
        const { command, data } = JSON.parse(message.data);
        if (command === "reauth") {
          const { method, idToken, result, rawNonce } = data;
          if (idToken) {
            handleSSOSignIn(method, idToken, rawNonce);
          } else {
            handleError(result);
          }
        }
      } catch {
        // ignore parse errors
      }
    };

    if ("ReactNativeWebView" in window) {
      if (window.kidsAppPlatform === "ios") {
        window.addEventListener("message", handleMessage);
      } else {
        document.addEventListener("message", handleMessage as EventListener);
      }
    }

    return () => {
      if ("ReactNativeWebView" in window) {
        if (window.kidsAppPlatform === "ios") {
          window.removeEventListener("message", handleMessage);
        } else {
          document.removeEventListener("message", handleMessage as EventListener);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // rawNonce はネイティブが Apple へ nonce を渡したときだけ送られてくる（Android）
  const handleSSOSignIn = async (method: string, idToken: string, rawNonce?: string) => {
    try {
      let credential;
      if (method === "google") {
        credential = GoogleAuthProvider.credential(idToken);
      } else if (method === "apple") {
        const provider = new OAuthProvider("apple.com");
        credential = provider.credential({ idToken, rawNonce });
      }
      if (credential) {
        await signInWithCredential(auth, credential);
        handleSuccess();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleSuccess = () => {
    updateLastSignInTime();
    handleDialog({ type: DialogType.LOADING, value: false });
    toast(
      <div>
        <p className="text text-textcolor-black">{I18n.t("MSG_SIGN_IN_SUCCESS_CONTENT")}</p>
      </div>,
    );
    onSuccess();
  };

  const handleError = (error: unknown) => {
    console.error("ReAuth error:", error);
    handleDialog({ type: DialogType.LOADING, value: false });
    setAuthError(true);
    setDisable(false);
  };

  // パスワード認証
  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disable || !password) return;

    play();
    setDisable(true);

    try {
      handleDialog({ type: DialogType.LOADING, value: true });
      await signInWithEmailAndPassword(auth, parent.email, password);
      handleSuccess();
    } catch (error: unknown) {
      handleDialog({ type: DialogType.LOADING, value: false });
      setDisable(false);
      const firebaseError = error as { code?: string };
      if (
        firebaseError?.code === "auth/wrong-password" ||
        firebaseError?.code === "auth/invalid-credential"
      ) {
        setPasswordError(true);
      } else {
        setAuthError(true);
      }
    }
  };

  // Google 再認証
  const handleGoogleReAuth = async () => {
    play();
    if ("ReactNativeWebView" in window) {
      handleDialog({ type: DialogType.LOADING, value: true });
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "reauth",
          data: { method: "google", email: parent.email },
        }),
      );
    } else if (currentUser) {
      try {
        handleDialog({ type: DialogType.LOADING, value: true });
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ login_hint: parent.email });
        await reauthenticateWithPopup(currentUser, provider);
        handleSuccess();
      } catch (error) {
        handleError(error);
      }
    }
  };

  // Apple 再認証
  const handleAppleReAuth = async () => {
    play();
    if ("ReactNativeWebView" in window) {
      handleDialog({ type: DialogType.LOADING, value: true });
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          command: "reauth",
          data: { method: "apple", email: parent.email },
        }),
      );
    } else if (currentUser) {
      try {
        handleDialog({ type: DialogType.LOADING, value: true });
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.setCustomParameters({ login_hint: parent.email });
        await reauthenticateWithPopup(currentUser, provider);
        handleSuccess();
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseError = () => {
    play();
    setAuthError(false);
  };

  // パスワードリセット
  const handlePasswordReset = async () => {
    play();
    setDisable(true);

    try {
      handleDialog({ type: DialogType.LOADING, value: true });
      const methods = await fetchSignInMethodsForEmail(auth, parent.email);
      if (methods.includes("password")) {
        await sendPasswordResetEmail(auth, parent.email);
      }
      handleDialog({ type: DialogType.LOADING, value: false });
      setView("sent");
    } catch (error) {
      console.error("Password reset error:", error);
      handleDialog({ type: DialogType.LOADING, value: false });
      toast(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_SIGN_PASSWORD_RESET_FAILED")}</p>
        </div>,
      );
      setDisable(false);
    }
  };

  const isPasswordProvider = providerId === UserProvider.PASSWORD;
  const isGoogleProvider = providerId === UserProvider.GOOGLE;
  const isAppleProvider = providerId === UserProvider.APPLE;

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[500px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              {/* タイトル */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">lock</span>
                <h2 className="text-[33px] text-textcolor-black">{I18n.t("MSG_REAUTH_TITLE")}</h2>
              </div>

              {/* ユーザー情報 */}
              <div className="flex-col-el flex-center mb-6">
                <UserAvatar size={120} icon={parent?.icon} />
                <div className="text-[24px] text-textcolor-black mt-2">{parent?.display_name}</div>
                <div className="text-[18px] text-gray2-100">{parent?.email}</div>
              </div>

              {/* エラーメッセージ */}
              {authError && (
                <WarningMessageBox
                  zIndex={999999}
                  title={I18n.t("MSG_SIGN_IN_FAILED_TITLE")}
                  message={I18n.t("MSG_SIGN_IN_FAILED_MESSAGE")}
                  close={handleCloseError}
                />
              )}

              {/* パスワード認証エリア */}
              {isPasswordProvider && (
                <div className="flex-col-el w-full h-[240px]">
                  {/* パスワード入力画面 */}
                  {view === "password" && (
                    <form className="flex-col-el w-full gap-4" onSubmit={handlePasswordSubmit}>
                      <div className="flex-col-el relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            setPasswordError(false);
                          }}
                          className={`input-form !bg-white text-body text-textcolor-black text-[24px] pr-12 ${
                            passwordError && "!border-danger"
                          }`}
                          placeholder={I18n.t("MSG_SIGN_USER_PASSWORD")}
                          autoComplete="current-password"
                        />
                        <div className="flex-col-el absolute right-0 top-0 h-[56px] justify-center pr-2">
                          <IconButton onClick={handleClickShowPassword} size="small">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </div>
                        <label
                          className={`text-body text-left text-[18px] h-5 text-danger ${
                            !passwordError && "invisible"
                          }`}
                        >
                          {I18n.t("MSG_SIGN_IN_CHANGE_PASSWORD_CURRENT_PASSWORD_WRONG")}
                        </label>
                      </div>

                      <div className="flex-row-el flex-center mt-2">
                        <button
                          type="submit"
                          disabled={!password || disable}
                          className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {I18n.t("MSG_SIGN_IN_BTN")}
                        </button>
                      </div>

                      {/* パスワードを忘れた場合リンク */}
                      <div className="flex-row-el flex-center mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            play();
                            setView("reset");
                          }}
                          className="text-[18px] text-blue-80 underline cursor-pointer bg-transparent border-none"
                        >
                          {I18n.t("MSG_SIGN_IN_FORGOT_PASSWORD")}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* パスワードリセット確認画面 */}
                  {view === "reset" && (
                    <div className="flex-col-el w-full gap-4">
                      <p className="text-[18px] text-gray2-100 text-center">
                        {I18n.t("MSG_REAUTH_PASSWORD_RESET_CONFIRM")}
                      </p>

                      <div className="flex-row-el flex-center mt-2">
                        <button
                          type="button"
                          onClick={handlePasswordReset}
                          disabled={disable}
                          className="flex-row-el items-center justify-center w-[240px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {I18n.t("MSG_SIGN_PASSWORD_RESET_SEND_BTN")}
                        </button>
                      </div>

                      {/* 戻るリンク */}
                      <div className="flex-row-el flex-center mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            play();
                            setView("password");
                            setDisable(false);
                          }}
                          className="text-[18px] text-blue-80 underline cursor-pointer bg-transparent border-none"
                        >
                          {I18n.t("MSG_REAUTH_BACK")}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 送信完了画面 */}
                  {view === "sent" && (
                    <div className="flex-col-el w-full gap-4">
                      <div className="flex-row-el flex-center gap-2">
                        <span className="material-symbols-outlined !text-[36px] text-green-100">
                          check_circle
                        </span>
                        <p className="text-[24px] text-textcolor-black">
                          {I18n.t("MSG_SIGN_EMAIL_SENT")}
                        </p>
                      </div>

                      <div className="flex-row-el flex-center mt-2">
                        <button
                          type="button"
                          onClick={onClose}
                          className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                        >
                          {I18n.t("MSG_BTN_CLOSE")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Google 再認証 */}
              {isGoogleProvider && (
                <div className="flex-col-el w-full gap-4">
                  <p className="text-[18px] text-gray2-100 text-center">
                    {I18n.t("MSG_REAUTH_SSO_MESSAGE")}
                  </p>
                  <div
                    onClick={handleGoogleReAuth}
                    className="flex-row-el flex-center w-full h-[42px] bg-[#fff] rounded-md border-solid border-[1px] border-[#dadce0] cursor-pointer"
                  >
                    <GoogleSignButton />
                    <p className="text-body text-[16px] text-textcolor-black">
                      {I18n.t("MSG_SIGN_IN_WITH_GOOGLE")}
                    </p>
                  </div>
                </div>
              )}

              {/* Apple 再認証 */}
              {isAppleProvider && (
                <div className="flex-col-el w-full gap-4">
                  <p className="text-[18px] text-gray2-100 text-center">
                    {I18n.t("MSG_REAUTH_SSO_MESSAGE")}
                  </p>
                  <div
                    onClick={handleAppleReAuth}
                    className="flex-row-el flex-center w-full h-[40px] bg-[#000] rounded-lg cursor-pointer"
                  >
                    <AppleIcon sx={{ fontSize: 16, marginRight: 0.5, color: "white" }} />
                    <p className="text-body text-[16px] text-white">
                      {I18n.t("MSG_SIGN_IN_WITH_APPLE")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
